<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <Globe class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Browser Sessions
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent class="space-y-6">
            <div class="space-y-6">
                <!-- Session List -->
                <div v-for="session in sessions" :key="session.id" class="flex items-center gap-3">
                    <Monitor class="size-6 text-new-neutral-400 shrink-0" />
                    <div class="space-y-1.5">
                        <p class="text-sm text-muted-foreground">
                            {{ session.agent.platform || 'Unknown' }} - {{ session.agent.browser || 'Unknown' }}
                        </p>
                        <div class="flex items-center gap-2 text-xs">
                            <span class="text-muted-foreground font-light">{{ session.ip_address }}</span>
                            <span v-if="session.is_current_device" class="font-semibold text-new-green-500">This device</span>
                            <span v-else class="text-new-neutral-400">{{ session.last_active }}</span>
                        </div>
                    </div>
                </div>

                <p class="text-xs leading-4 text-muted-foreground max-w-[405px]">
                    If necessary, you may log out of all of your other browser sessions across all of your devices. Some of your recent sessions are listed below; however, this list may not be exhaustive. If you feel your account has been compromised, you should also update your password.
                </p>
            </div>

            <component
                :is="confirmComponent"
                :skip-confirmation="true"
                title="Log Out Other Browser Sessions"
                button="Log Out Other Browser Sessions"
                content="Please enter your password to confirm you would like to log out of your other browser sessions across all of your devices."
                @confirmed="logoutOtherSessions"
            >
                <Button :disabled="loading">
                    Log out all other sessions
                </Button>
            </component>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { ref, computed, PropType } from 'vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { create } from '@composables/useNotifications';
import { Globe, Monitor } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ConfirmsPassword from './ConfirmsPasswordV2.vue';
import ConfirmsOtp from './ConfirmsOtpV2.vue';
import ISession from '@/types/Settings/ISession';

const props = defineProps({
    sessions: {
        type: Array as PropType<Array<ISession>>,
        required: true,
    },
    hasPassword: {
        type: Boolean,
        required: true,
    },
});

const confirmComponent = computed(() =>
    props.hasPassword ? ConfirmsPassword : ConfirmsOtp
);

const loading = ref(false);

const logoutOtherSessions = (password?: string) => {
    loading.value = true;

    router.delete(route('other-browser-sessions.destroy'), {
        data: { password: password || '' },
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Other browser sessions have been logged out');
        },
        onError: (err) => {
            const msg = err.password || err.code || 'Failed to log out other sessions';
            toast.error(msg);
        },
        onFinish: () => {
            loading.value = false;
        },
    });
};
</script>
